/**
 * @file CommunePage.tsx
 * @summary Page lieu / commune
 *
 * Page SEO locale ciblant une commune autour de Saint-Pierre-de-Chandieu (silo géographique).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import { useParams, Navigate, Link } from "react-router-dom";
import SEO from "@/components/SEO";
import ServicePageLayout from "@/components/ServicePageLayout";
import { findCommune, communes } from "@/data/communes";

export default function CommunePage() {
  const { slug = "" } = useParams();
  const commune = findCommune(slug);

  if (!commune) return <Navigate to="/lieux" replace />;

  const URL = `https://crossfitchistera.fr/lieux/${commune.slug}`;
  const title = `CrossFit ${commune.name} (${commune.cp}) — Box affiliée à ${commune.drive_min} min | Chistera`;
  const description = `Box CrossFit affiliée la plus proche de ${commune.name} : ${commune.distance_km} km via ${commune.axe}. Cours tous niveaux, 2 séances d'essai gratuites.`;

  // 3 communes voisines (hors la courante)
  const neighbors = [...communes]
    .filter((c) => c.slug !== commune.slug)
    .sort((a, b) => Math.abs(a.distance_km - commune.distance_km) - Math.abs(b.distance_km - commune.distance_km))
    .slice(0, 4);

  const faq = [
    { "@type": "Question", name: `Y a-t-il un CrossFit à ${commune.name} ?`, acceptedAnswer: { "@type": "Answer", text: `La box CrossFit affiliée la plus proche de ${commune.name} est CrossFit Chistera, située à Saint-Pierre-de-Chandieu (${commune.distance_km} km, ${commune.drive_min} min en voiture via ${commune.axe}).` } },
    { "@type": "Question", name: `Combien de temps pour aller de ${commune.name} à CrossFit Chistera ?`, acceptedAnswer: { "@type": "Answer", text: `Comptez environ ${commune.drive_min} minutes en voiture par ${commune.axe}.` } },
    { "@type": "Question", name: "Faut-il être expérimenté pour s'inscrire ?", acceptedAnswer: { "@type": "Answer", text: "Non. Les 2 premières séances sont gratuites et adaptées à votre niveau, du débutant complet au compétiteur." } },
  ];

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: `CrossFit pour habitants de ${commune.name}`,
      provider: { "@id": "https://crossfitchistera.fr/#localbusiness" },
      areaServed: { "@type": "City", name: commune.name, address: { "@type": "PostalAddress", postalCode: commune.cp, addressCountry: "FR" } },
      url: URL,
      description,
    },
    { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faq },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: "https://crossfitchistera.fr/" },
        { "@type": "ListItem", position: 2, name: "Lieux desservis", item: "https://crossfitchistera.fr/lieux" },
        { "@type": "ListItem", position: 3, name: commune.name, item: URL },
      ],
    },
  ];

  return (
    <>
      <SEO title={title} description={description} canonical={URL} jsonLd={jsonLd} />
      <ServicePageLayout
        breadcrumbs={[{ label: "Lieux desservis", to: "/lieux" }, { label: commune.name }]}
        h1={`CrossFit pour les habitants de ${commune.name}`}
        intro={`CrossFit Chistera, la box affiliée la plus proche de ${commune.name}, est située à Saint-Pierre-de-Chandieu — ${commune.distance_km} km / ${commune.drive_min} min via ${commune.axe}. Cours collectifs encadrés par des coachs diplômés, tous niveaux, 2 séances d'essai gratuites.`}
      >
        <h2>Depuis {commune.name}, en pratique</h2>
        <p>{commune.hook}</p>
        <p>
          La box est ouverte 6 jours sur 7, avec des créneaux du matin (6h, 9h), du midi (12h) et du soir (17h30, 18h30, 19h30) — vous trouverez forcément un cours qui s'intègre à votre vie professionnelle et familiale.
        </p>

        <h2>Pourquoi venir s'entraîner depuis {commune.name} ?</h2>
        <ul>
          <li><strong>Box neuve de 600 m²</strong> entièrement équipée (rigs, barres olympiques, haltères, kettlebells, rameurs Concept2, ski erg, assault bike, sled).</li>
          <li><strong>Cours à 12 personnes maximum</strong> pour une correction technique individuelle.</li>
          <li><strong>Coachs diplômés</strong> : Anthony Bendahmane (Master STAPS, CrossFit Level 1) et Cédric Bastien (BPJEPS, CrossFit Level 1).</li>
          <li><strong>Tous niveaux accueillis</strong> — du débutant complet au compétiteur Hyrox.</li>
          <li><strong>Affiliation officielle CrossFit</strong> reconnue mondialement.</li>
        </ul>

        <h2>Cours disponibles depuis {commune.name}</h2>
        <ul>
          <li><Link to="/cours/wod">Cours WOD CrossFit</Link> — 1 heure encadrée, 6 jours/7.</li>
          <li><Link to="/cours/hybrid-training">Hybrid Training</Link> — préparation Hyrox & ATHX.</li>
          <li><Link to="/crossfit-debutant">CrossFit débutant</Link> — pour démarrer sans expérience.</li>
          <li><Link to="/reprendre-le-sport">Reprendre le sport</Link> — adapté à tous les âges.</li>
        </ul>

        <h2>Tarifs</h2>
        <p>
          Abonnements à partir de <strong>64 €/mois</strong> (2 séances/semaine), 74 €/mois (3 séances/semaine), 90 €/mois (illimité). Drop-in à 15 €. <strong>2 séances d'essai gratuites</strong> pour les habitants de {commune.name}.
        </p>

        <h2>Communes voisines également desservies</h2>
        <ul>
          {neighbors.map((n) => (
            <li key={n.slug}>
              <Link to={`/lieux/${n.slug}`}>CrossFit {n.name}</Link> — {n.distance_km} km de la box.
            </li>
          ))}
        </ul>

        <h2>Questions fréquentes</h2>
        <h3>Y a-t-il un CrossFit à {commune.name} ?</h3>
        <p>La box affiliée la plus proche est CrossFit Chistera, à Saint-Pierre-de-Chandieu, {commune.drive_min} min en voiture.</p>
        <h3>Quel est le temps de trajet ?</h3>
        <p>Environ {commune.drive_min} minutes en voiture via {commune.axe}.</p>
        <h3>Faut-il être expérimenté ?</h3>
        <p>Non. Tout est adapté à votre niveau. Les 2 premières séances sont gratuites.</p>
      </ServicePageLayout>
    </>
  );
}
